import React, { useEffect, useRef, useState } from "react";

/**
 * Typewriter — types out `text` character-by-character with a blinking caret.
 *
 * Triggers automatically when the element enters the viewport, so it feels
 * "alive" even when the user has just scrolled to it. Restart on re-mount.
 */
export default function Typewriter({
  text,
  speed = 32,         // ms per character
  delay = 200,        // initial delay before typing starts
  caretColor = "var(--qt-cyan)",
  className,
  style,
  testid,
}) {
  const [shown, setShown] = useState("");
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            // Reset state when it comes into view
            setShown("");
            setDone(false);
            let i = 0;
            const start = () => {
              timerRef.current = setInterval(() => {
                i += 1;
                setShown(text.slice(0, i));
                if (i >= text.length) {
                  clearInterval(timerRef.current);
                  setDone(true);
                }
              }, speed);
            };
            setTimeout(start, delay);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [text, speed, delay]);

  return (
    <div ref={ref} data-testid={testid} className={className}
         style={{ minHeight: "1.5em", ...style }}>
      {shown}
      <span aria-hidden
            style={{
              display: "inline-block",
              width: 2, height: "1em", marginLeft: 4,
              verticalAlign: "-0.15em",
              background: caretColor,
              animation: done ? "qt-caret-blink 1s step-end infinite" : "qt-caret-solid 1s step-end infinite",
            }} />
      <style>{`
        @keyframes qt-caret-blink {
          50% { opacity: 0; }
        }
        @keyframes qt-caret-solid {
          0%, 100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
